"use client";

import { Flag, Link2, Lock, Quote, Send, Share2, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import type { ActivityPostFeedItem } from "@/actions/activity-post";
import { FeedActionRow } from "@/components/feed/feed-action-row";
import {
  FEED_X_BODY,
  FeedCardHeader,
} from "@/components/feed/feed-card-header";
import {
  FeedMediaViewer,
  type FeedMediaViewerItem,
} from "@/components/feed/feed-media-viewer";
import { FeedVideo } from "@/components/feed/feed-video";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer";
import { ReportFeedItemDialog } from "@/components/feed/report-feed-item-dialog";
import { ActivityVisualization } from "@/components/posts/activity-visualization";
import {
  CommentsSheet,
  type CommentsSheetViewer,
} from "@/components/posts/comments-sheet";
import { ExpandablePostText } from "@/components/posts/expandable-post-text";
import { LikesSheet } from "@/components/posts/likes-sheet";
import { PostLinkPreview } from "@/components/posts/post-link-preview";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { MediaSoundButton } from "@/components/ui/media-sound-button";
import type { MediaData } from "@/db/schema/post";
import { useToggleActivityPostLikeMutation } from "@/hooks/use-activity-comments";
import {
  useDeleteFeedItemMutation,
  useTrackFeedShareMutation,
} from "@/hooks/use-feed-share";
import { formatRelativeTimeCompact } from "@/lib/dates/relative-time";
import type { FeedEntityQuoteTarget } from "@/lib/feed/feed-entity-quote";
import { extractFirstFeedUrl } from "@/lib/feed/rich-content";
import { toRenderableMedia } from "@/lib/media";
import { absolutePublicAppUrl } from "@/lib/public-app-url";
import { cn } from "@/lib/utils";
import { isWeightTrainingWorkoutPostPayload } from "@/lib/weight-training/workout-post-payload";

interface ActivityPostProps {
  post: ActivityPostFeedItem;
  viewer?: CommentsSheetViewer;
  onOpenComments?: (postId: number) => void;
  suppressDetailLink?: boolean;
  embedded?: boolean;
  /** Notification deep link: open comments to this comment and highlight it. */
  deepLinkCommentId?: number | null;
}

export function ActivityPost({
  post,
  viewer,
  suppressDetailLink = false,
  embedded = false,
  deepLinkCommentId,
}: ActivityPostProps) {
  const [isLiked, setIsLiked] = useState(post.isLikedByViewer);
  const [likeCount, setLikeCount] = useState(post.likeCount);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [showComments, setShowComments] = useState(deepLinkCommentId != null);
  const [showLikes, setShowLikes] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendInChat, setShowSendInChat] = useState(false);
  const [shareCount, setShareCount] = useState(post.shareCount);
  const [mediaViewerIndex, setMediaViewerIndex] = useState<number | null>(null);

  // Per-instance salt so the same activity post rendered twice (feed + quote
  // embed) never collides in the video autoplay/audio registry.
  const mediaAudioSeed = useId();
  // The activity visualization is the first slide of the media carousel; its
  // rendered height sizes every photo/video slide so the whole set shares the
  // card's aspect ratio instead of stacking below at a different shape.
  const vizRef = useRef<HTMLDivElement>(null);
  const [vizHeight, setVizHeight] = useState(240);
  const toggleLikeMutation = useToggleActivityPostLikeMutation();
  const trackShareMutation = useTrackFeedShareMutation();
  const deleteFeedItemMutation = useDeleteFeedItemMutation();
  const isPrivatePreview = Boolean(post.isPrivatePreview);
  const isOwner = Boolean(viewer?.id && viewer.id === post.author.id);

  const createdAtDate = new Date(post.createdAt);
  const timeAgo = formatRelativeTimeCompact(createdAtDate);
  const isAuthorBlocked = Boolean(post.author.isBlocked);
  const authorHandle = post.author.username;

  useEffect(() => {
    setShareCount(post.shareCount);
  }, [post.shareCount]);

  useEffect(() => {
    const el = vizRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) setVizHeight(h);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLike = () => {
    if (isPrivatePreview) {
      return;
    }

    const previousLiked = isLiked;
    const previousCount = likeCount;

    // Optimistic update
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount((prev) => prev + (newLiked ? 1 : -1));

    toggleLikeMutation.mutate(
      { activityPostId: post.id, currentlyLiked: previousLiked },
      {
        onError: () => {
          // Rollback on error
          setIsLiked(previousLiked);
          setLikeCount(previousCount);
          toast.error("Couldn't update like right now");
        },
      },
    );
  };

  const getShareUrl = () => absolutePublicAppUrl(`/activity-post/${post.id}`);

  const recordShare = (
    target: "system" | "copy" | "chat" | "native" | "story",
  ) => {
    trackShareMutation.mutate(
      { entityType: "activity_post", entityId: post.id, target },
      {
        onSuccess: (result) => {
          setShareCount(result.shareCount);
        },
      },
    );
  };

  const handleOpenQuote = () => {
    if (isPrivatePreview) return;
    setShowActions(false);
    setShowQuote(true);
  };

  const handleOpenSendInChat = () => {
    if (isPrivatePreview) return;
    setShowActions(false);
    setShowSendInChat(true);
  };

  const handleOpenDeleteConfirm = () => {
    setShowActions(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteFromFeed = () => {
    deleteFeedItemMutation.mutate(
      { entityType: "activity_post", entityId: post.id },
      {
        onSuccess: () => {
          toast.success("Removed from feed");
          setShowDeleteConfirm(false);
        },
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : "Couldn't remove this feed item",
          );
        },
      },
    );
  };

  const handleCopyLink = async () => {
    if (isPrivatePreview) return;
    try {
      await navigator.clipboard.writeText(getShareUrl());
      recordShare("copy");
      toast.success("Link copied");
      setShowActions(false);
    } catch (_error) {
      toast.error("Couldn't copy link");
    }
  };

  const handleShare = () => {
    if (isPrivatePreview) return;
    setShowActions(false);
    setShowSendInChat(true);
  };

  // Parse template data
  const rawData = post.data as
    | (Record<string, unknown> & {
        mediaUrls?: MediaData[];
      })
    | null;

  const mediaItems = (() => {
    if (!rawData?.mediaUrls?.length) return [] as MediaData[];
    return [...rawData.mediaUrls].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  })();

  const renderableMediaItems = mediaItems
    .map((item) => toRenderableMedia(item))
    .filter((item): item is NonNullable<ReturnType<typeof toRenderableMedia>> =>
      Boolean(item),
    );
  const quoteTarget = {
    entityType: "activity_post",
    entityId: post.id,
    item: post,
  } satisfies FeedEntityQuoteTarget;
  const mediaViewerItems: FeedMediaViewerItem[] = renderableMediaItems.map(
    (item) => ({
      key: `activity-post-${post.id}-${item.id}`,
      kind: item.kind,
      src: item.url,
      hlsSrc: item.hls?.url,
      posterSrc: item.poster?.url ?? null,
      blurHash: item.blurHash,
      // Processed video only has its HLS playlist (`url` is the .m3u8).
      downloadSrc: item.kind === "video" && !item.hls ? item.url : null,
    }),
  );
  const feedLinkPreview = extractFirstFeedUrl(post.content);

  const visualizationData = (() => {
    if (!rawData) return null;
    const { mediaUrls: _mediaUrls, ...rest } = rawData;
    return rest;
  })();

  const data = visualizationData as {
    mode?: string;
    scores?: string[];
    positions?: Record<string, string>;
  } | null;
  const showWorkoutDetailLink =
    !isPrivatePreview &&
    !suppressDetailLink &&
    isWeightTrainingWorkoutPostPayload(visualizationData);

  return (
    <article
      className={cn(
        "border-b border-border bg-background",
        embedded && "border-b-0",
      )}
    >
      {/* Header */}
      <FeedCardHeader
        layout={embedded ? "row" : "x"}
        displayName={post.author.displayName || ""}
        handle={authorHandle}
        avatarUrl={post.author.avatarUrl}
        createdAt={createdAtDate}
        timeAgo={timeAgo}
        isBlocked={isAuthorBlocked}
      />
      <div className={embedded ? undefined : FEED_X_BODY}>
        {/* Text leads the card (X/Twitter-style), two lines behind "more". */}
        {!isPrivatePreview && post.content ? (
          <div className="px-3 pt-1 pb-2">
            <ExpandablePostText
              content={post.content}
              mentions={post.mentions}
              clampLines={2}
              textClassName="text-[15px]"
            />
          </div>
        ) : null}

        {/* Activity card + media as one carousel: the visualization is slide 0
          and every photo/video after it is sized to the card's height, so the
          whole set shares one aspect ratio instead of stacking below. */}
        <div
          className={cn(
            "relative",
            embedded ? "px-2 pb-2" : "px-3",
            isPrivatePreview &&
              "after:absolute after:inset-0 after:bg-black/30 after:content-['']",
          )}
        >
          {renderableMediaItems.length > 0 ? (
            <Carousel className="w-full" opts={{ loop: true }}>
              <div
                className={cn(
                  "overflow-hidden border border-border/60",
                  embedded ? "rounded-xl" : "rounded-2xl",
                  isPrivatePreview && "scale-[1.02] blur-md",
                )}
              >
                <CarouselContent>
                  <CarouselItem className="pl-0">
                    <div ref={vizRef}>
                      <ActivityVisualization
                        activityId={post.activityId}
                        data={visualizationData}
                        participants={post.participants}
                      />
                    </div>
                  </CarouselItem>
                  {renderableMediaItems.map((item, index) => (
                    <CarouselItem
                      key={`${post.id}-${item.id}`}
                      className="relative pl-0"
                    >
                      <button
                        type="button"
                        className="relative block w-full overflow-hidden bg-surface-alt text-left"
                        style={{ height: vizHeight }}
                        onClick={() => {
                          if (!isPrivatePreview) {
                            setMediaViewerIndex(index);
                          }
                        }}
                        aria-label="Open media"
                      >
                        {item.kind === "video" ? (
                          <FeedVideo
                            audioId={`${mediaAudioSeed}-${item.id}`}
                            src={item.url}
                            mimeType={item.mimeType || "video/mp4"}
                            posterUrl={item.poster?.url}
                            blurHash={item.blurHash}
                            className="h-full w-full object-cover"
                            forceMuted={isPrivatePreview}
                          />
                        ) : (
                          <Image
                            src={item.url}
                            alt="Post media"
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, 600px"
                          />
                        )}
                      </button>
                      {!isPrivatePreview && item.kind === "video" ? (
                        <MediaSoundButton
                          audioId={`${mediaAudioSeed}-${item.id}`}
                        />
                      ) : null}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </div>
              {/* Dots live below the card so they never overlap the movement or
                workout stat strip that sits at the bottom of slide 0. */}
              <div className="relative mt-2 h-2.5">
                <CarouselDots className="bottom-0" />
              </div>
            </Carousel>
          ) : (
            <div
              className={cn(
                "overflow-hidden border border-border/60",
                embedded ? "rounded-xl" : "rounded-2xl",
                isPrivatePreview && "scale-[1.02] blur-md",
              )}
            >
              <div ref={vizRef}>
                <ActivityVisualization
                  activityId={post.activityId}
                  data={visualizationData}
                  participants={post.participants}
                />
              </div>
            </div>
          )}
          {isPrivatePreview && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-glass-border-20 bg-black/55 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
                <Lock className="h-4 w-4" />
                Private post
              </span>
            </div>
          )}
        </div>
        {mediaViewerIndex !== null && mediaViewerItems.length > 0 ? (
          <FeedMediaViewer
            items={mediaViewerItems}
            initialIndex={mediaViewerIndex}
            onClose={() => setMediaViewerIndex(null)}
            shareUrl={getShareUrl()}
            fileBaseName={`hiive-activity-${post.id}`}
            onShared={recordShare}
            post={{
              author: {
                displayName:
                  post.author.displayName || post.author.username || "User",
                handle: isAuthorBlocked ? null : authorHandle,
                avatarUrl: isAuthorBlocked ? null : post.author.avatarUrl,
              },
              caption: post.content,
              likeCount,
              commentCount,
              isLiked,
              onLike: handleLike,
              onComment: () => setShowComments(true),
              // The quote composer sits under the viewer, so close it first.
              onRepost: isPrivatePreview
                ? undefined
                : () => {
                    setMediaViewerIndex(null);
                    handleOpenQuote();
                  },
              onShare: handleShare,
              likeDisabled: toggleLikeMutation.isPending,
              shareDisabled: isPrivatePreview || trackShareMutation.isPending,
            }}
          />
        ) : null}

        {isPrivatePreview ? (
          <section className="px-4 py-3">
            <p className="text-sm text-text-secondary">
              This post is private. Only buddies can view full content.
            </p>
          </section>
        ) : (
          <>
            {/* Link preview stays below the media, not with the leading text. */}
            {feedLinkPreview && (
              <div className={cn("px-3 pt-2", embedded && "pb-2.5")}>
                <PostLinkPreview link={feedLinkPreview} compact />
              </div>
            )}

            {showWorkoutDetailLink && (
              <div className="px-3 pt-2">
                <BackAwareLink
                  href={`/activity-post/${post.id}`}
                  className="inline-flex items-center rounded-full border border-border px-3 py-1 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary"
                >
                  View more
                </BackAwareLink>
              </div>
            )}

            {data?.scores && data.scores.length > 0 && (
              <section className="flex flex-col gap-2 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-secondary">Score:</span>
                  <span className="font-semibold text-text-primary">
                    {data.scores.join(", ")}
                  </span>
                </div>
              </section>
            )}

            {!embedded ? (
              <FeedActionRow
                layout="x"
                className="px-3"
                likeCount={likeCount}
                commentCount={commentCount}
                shareCount={shareCount}
                isLiked={isLiked}
                onLike={handleLike}
                onOpenLikes={() => setShowLikes(true)}
                onComment={() => setShowComments(true)}
                onQuote={handleOpenQuote}
                onShare={handleShare}
                onMore={() => setShowActions(true)}
                likeDisabled={toggleLikeMutation.isPending}
                quoteDisabled={isPrivatePreview}
                shareDisabled={isPrivatePreview || trackShareMutation.isPending}
              />
            ) : null}

            <Drawer open={showActions} onOpenChange={setShowActions} modal>
              <DrawerContent className="border-t border-border outline-none">
                <DrawerTitle className="sr-only">
                  Activity post actions
                </DrawerTitle>
                <div className="mx-auto w-full max-w-lg p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={handleShare}
                    >
                      <Share2 className="h-5 w-5" />
                      Share
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={handleOpenSendInChat}
                    >
                      <Send className="h-5 w-5" />
                      Send in chat
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={handleCopyLink}
                    >
                      <Link2 className="h-5 w-5" />
                      Copy link
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={handleOpenQuote}
                    >
                      <Quote className="h-5 w-5" />
                      Quote repost
                    </Button>
                    {!isOwner ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                        onClick={() => {
                          setShowActions(false);
                          setShowReport(true);
                        }}
                      >
                        <Flag className="h-5 w-5" />
                        Report
                      </Button>
                    ) : null}
                    {isOwner ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-12 w-full justify-start rounded-xl px-4 text-destructive"
                        onClick={handleOpenDeleteConfirm}
                        disabled={deleteFeedItemMutation.isPending}
                      >
                        <Trash2 className="h-5 w-5" />
                        Delete from feed
                      </Button>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3 h-12 w-full rounded-xl"
                    onClick={() => setShowActions(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </DrawerContent>
            </Drawer>

            {/* Comments Sheet */}
            <CommentsSheet
              entityType="activity"
              entityId={post.id}
              open={showComments}
              onOpenChange={setShowComments}
              viewer={viewer}
              canModerate={isOwner}
              onCommentCountChange={setCommentCount}
              targetCommentId={deepLinkCommentId}
              threadContext={{
                author: post.author,
                text: post.content ?? post.activityName,
                createdAt: post.createdAt,
              }}
            />

            {/* Likes Sheet */}
            <LikesSheet
              entityType="activity"
              activityPostId={post.id}
              open={showLikes}
              onOpenChange={setShowLikes}
              currentUserId={viewer?.id}
            />
            {!isOwner ? (
              <ReportFeedItemDialog
                open={showReport}
                onOpenChange={setShowReport}
                targetUserId={post.author.id}
                targetLabel="activity post"
                entityType="activity_post"
                entityId={post.id}
              />
            ) : null}
            {showQuote ? (
              <RegularPostDrawer
                open={showQuote}
                onOpenChange={setShowQuote}
                currentUser={{
                  id: viewer?.id ?? "",
                  displayName: viewer?.displayName ?? "You",
                  avatarUrl: viewer?.avatarUrl ?? null,
                }}
                quoteTarget={{
                  kind: "feed_entity",
                  target: quoteTarget,
                  defaultVisibility: "private",
                }}
                onSuccess={() => setShowQuote(false)}
              />
            ) : null}
            <Dialog
              open={showDeleteConfirm}
              onOpenChange={setShowDeleteConfirm}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete from feed?</DialogTitle>
                  <DialogDescription>
                    This removes the activity from your feed without deleting
                    the underlying activity record.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteFeedItemMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteFromFeed}
                    disabled={deleteFeedItemMutation.isPending}
                  >
                    {deleteFeedItemMutation.isPending
                      ? "Deleting..."
                      : "Delete"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <SendToChatDrawer
              open={showSendInChat}
              onOpenChange={setShowSendInChat}
              payload={{
                type: "entity",
                entityType: "activity_post",
                entityId: String(post.id),
              }}
              nativeShare={{
                title: post.activityName
                  ? `${post.activityName} on Hiive`
                  : "Hiive activity",
                text: post.content ?? "View this activity on Hiive",
                url: getShareUrl(),
              }}
              onShared={recordShare}
            />
          </>
        )}
      </div>
    </article>
  );
}
